import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	collection,
	collectionGroup,
	doc,
	getDocs,
	onSnapshot,
	orderBy,
	query,
	serverTimestamp,
	setDoc,
	where,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { fromStored } from './warmupShape';

/**
 * The catalog, this reviewer's own verdicts, and (for an admin) everyone's.
 *
 * THREE READS, DELIBERATELY DIFFERENT SHAPES:
 *
 *  - the CATALOG is a one-shot `getDocs`, not a listener. It is seeded from a
 *    file and closed to client writes (`allow write: if false`), so it cannot
 *    change while the page is open and a subscription would only cost reads.
 *  - THIS REVIEWER'S reviews are a live `onSnapshot` on a collectionGroup
 *    scoped to her own uid, so a verdict saved on her phone shows on her laptop
 *    without a refresh — and, more usefully, so the "saved" state reflects the
 *    database rather than local optimism.
 *  - EVERY reviewer's reviews load only for an admin, only when the summary is
 *    opened. `firestore.rules` denies the unscoped collectionGroup to a teacher,
 *    so this must not run for one: a denied listener logs a permission error and
 *    reads as a broken page.
 *
 * The reviewer-scoped query needs no composite index — a collectionGroup with a
 * single equality on `__name__`-adjacent data would, so it is expressed as a
 * `where` on the stored `reviewerUid` field instead. That field duplicates the
 * doc id on purpose; the RULES still key on the path segment, which cannot be
 * forged, and the field exists only to make this one query possible.
 */

const COLLECTION = 'warmupExercises';
const SAVE_DEBOUNCE_MS = 600;

export const useWarmupCatalog = ({ uid, displayName, isAdmin }) => {
	const [catalog, setCatalog] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState('');

	// exerciseId -> { verdict, note, updatedAt } for THIS reviewer
	const [myReviews, setMyReviews] = useState({});
	// exerciseId -> [{ reviewerUid, updatedByName, verdict, note }] for everyone
	const [allReviews, setAllReviews] = useState(null);
	const [allReviewsError, setAllReviewsError] = useState('');

	// exerciseId -> 'saving' | 'saved' | 'error'
	const [saveState, setSaveState] = useState({});
	const timers = useRef({});
	const pending = useRef({});

	// ---- catalog -----------------------------------------------------------
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const snap = await getDocs(query(collection(db, COLLECTION), orderBy('sortKey')));
				if (cancelled) return;
				setCatalog(snap.docs.map((d) => fromStored(d.id, d.data())));
				setLoadError('');
			} catch (err) {
				if (cancelled) return;
				console.error('Error loading warmup catalog:', err);
				// Named honestly: an empty catalog and a denied read look identical
				// on screen, and only one of them is fixed by seeding.
				setLoadError(
					err?.code === 'permission-denied'
						? 'You do not have access to the warmup catalog.'
						: 'Could not load the warmup catalog.'
				);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	// ---- my own reviews, live ---------------------------------------------
	useEffect(() => {
		if (!uid) return undefined;
		// SCOPED TO HER OWN UID, and the `where` is load-bearing rather than a
		// convenience: firestore.rules only satisfies a teacher's collectionGroup
		// read when the query carries this exact filter. Without it the listener
		// is denied outright — which is what an earlier cut of this hook did.
		const q = query(collectionGroup(db, 'reviews'), where('reviewerUid', '==', uid));
		const unsub = onSnapshot(
			q,
			(snap) => {
				const mine = {};
				snap.docs.forEach((d) => {
					const exerciseId = d.ref.parent.parent?.id;
					if (exerciseId) mine[exerciseId] = d.data();
				});
				setMyReviews(mine);
			},
			(err) => console.error('Error subscribing to my warmup reviews:', err)
		);
		return () => unsub();
	}, [uid]);

	// ---- everyone's reviews, admin only, on demand -------------------------
	const loadAllReviews = useCallback(async () => {
		if (!isAdmin) return;
		try {
			const snap = await getDocs(collectionGroup(db, 'reviews'));
			const byExercise = {};
			snap.docs.forEach((d) => {
				const exerciseId = d.ref.parent.parent?.id;
				if (!exerciseId) return;
				byExercise[exerciseId] = byExercise[exerciseId] || [];
				byExercise[exerciseId].push({ reviewerUid: d.id, ...d.data() });
			});
			setAllReviews(byExercise);
			setAllReviewsError('');
		} catch (err) {
			console.error('Error loading all warmup reviews:', err);
			setAllReviewsError('Could not load the reviews.');
		}
	}, [isAdmin]);

	// ---- writing a verdict / note -----------------------------------------
	const flush = useCallback(
		async (exerciseId) => {
			const patch = pending.current[exerciseId];
			if (!patch || !uid) return;
			delete pending.current[exerciseId];
			setSaveState((s) => ({ ...s, [exerciseId]: 'saving' }));
			try {
				await setDoc(
					doc(db, COLLECTION, exerciseId, 'reviews', uid),
					{
						...patch,
						reviewerUid: uid,
						updatedByName: displayName || '',
						updatedAt: serverTimestamp(),
					},
					{ merge: true }
				);
				// "saved" ONLY after the write resolves — the handoff asks for the
				// saved state to be honest, and an optimistic tick on a failing
				// write is exactly the lie it is guarding against.
				setSaveState((s) => ({ ...s, [exerciseId]: 'saved' }));
			} catch (err) {
				console.error('Error saving warmup review:', err);
				setSaveState((s) => ({ ...s, [exerciseId]: 'error' }));
			}
		},
		[uid, displayName]
	);

	/**
	 * Queue a change. Verdict taps write almost immediately (a tap is a decision
	 * and should land); typing debounces, so a note is one write per pause
	 * rather than one per keystroke.
	 */
	const saveReview = useCallback(
		(exerciseId, patch, { immediate = false } = {}) => {
			pending.current[exerciseId] = { ...(pending.current[exerciseId] || {}), ...patch };
			setSaveState((s) => ({ ...s, [exerciseId]: 'saving' }));
			clearTimeout(timers.current[exerciseId]);
			timers.current[exerciseId] = setTimeout(
				() => flush(exerciseId),
				immediate ? 0 : SAVE_DEBOUNCE_MS
			);
		},
		[flush]
	);

	// A pending note must not be lost by navigating away mid-sentence.
	// `beforeunload` does NOT fire on an in-app route change (CLAUDE.md), so the
	// unmount cleanup is the one that matters here; refs keep it to a single
	// subscription rather than one per keystroke.
	const flushRef = useRef(flush);
	flushRef.current = flush;
	useEffect(
		() => () => {
			Object.keys(timers.current).forEach((id) => clearTimeout(timers.current[id]));
			Object.keys(pending.current).forEach((id) => flushRef.current(id));
		},
		[]
	);

	const byPurpose = useMemo(() => {
		const map = {};
		catalog.forEach((c) => {
			map[c.purpose] = map[c.purpose] || [];
			map[c.purpose].push(c);
		});
		return map;
	}, [catalog]);

	return {
		catalog,
		byPurpose,
		isLoading,
		loadError,
		myReviews,
		saveReview,
		saveState,
		allReviews,
		allReviewsError,
		loadAllReviews,
	};
};

export default useWarmupCatalog;
